import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  Rate,
  Checkbox,
  message,
  Tooltip,
  Skeleton,
} from "antd";
import { Box, useMediaQuery } from "@mui/material";
import {
  LeftCircleFilled,
  RightCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  initializeJotURL,
  getJotURLInstance,
} from "../../services/api/JotURLService";
import { Snackbar, Alert } from "@mui/material";

import { UIRecommendationAPIService } from "../../services/api/UIRecommendationAPIService";
import { formatNumber } from "../../shared/utils";
import logoIcon from "../../assests/Images/kangaroo.webp";
import leftArrow from "../../assests/Images/left-arrow-svgrepo-com.svg";
import rightArrow from "../../assests/Images/right-arrow-svgrepo-com.svg";
import { ProductItem } from "../../components/common/ProductItem";
import FooterSummary from "../../components/common/FooterSummary";
import ProductCardSkeleton from "../../components/common/ProductCardSkeleton";
import { shoppingListTrackingService } from "../../services/api/shoppingListTrackingService";
import { ProductItemV2 } from "../../components/common/ProductItemV2";

const { Title, Text } = Typography;

// Skeleton Category Section Component
const CategorySectionSkeleton = () => {
  const scrollContainerRef = useRef(null);

  return (
    <div style={{ marginBottom: "10px" }}>
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Skeleton.Input
          active
          size="default"
          style={{ width: "200px", height: "32px" }}
        />
      </div> */}

      <div style={{ position: "relative" }}>
        <div
          ref={scrollContainerRef}
          style={{
            display: "flex",
            overflowX: "auto",
            scrollBehavior: "smooth",
            paddingBottom: "8px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
          className="hide-scrollbar"
        >
          {[0, 1, 2].map((index) => (
            <ProductCardSkeleton key={index} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CategorySection = ({
  category,
  index,
  selectedProducts,
  onToggleProduct,
  maxSelectionReached,
  addToCard,
  defaultStore = "Amazon",
}) => {
  const scrollContainerRef = useRef(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [sortBy, setSortBy] = useState("bestValue");
  const [dataToShow, setDataToShow] = useState(category.products);
  const titleRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [category.name]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        left: 0,
        behavior: "smooth",
      });

      // reset arrow visibility
      setShowLeftButton(false);
      setShowRightButton(container.scrollWidth > container.clientWidth);
    }
  }, [sortBy]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -200 : 200;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftButton(container.scrollLeft > 0);
      setShowRightButton(
        container.scrollLeft <
          container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const filteredProducts = useMemo(() => {
    const source =
      sortBy === "bestValue"
        ? category.products
        : category.cheapest_products_list;
    return defaultStore
      ? source.filter(
          (product) =>
            product.store.toLowerCase() === defaultStore.toLowerCase()
        )
      : source;
  }, [defaultStore, sortBy]);

  useEffect(() => {
    setDataToShow(filteredProducts);
  }, [sortBy]);

  return (
    <div>
      <div className="flex items-center mb-4">
        {/* LEFT — Title (50%) */}
        <div
          className="basis-[40%]
      max-w-[40%]
      md:basis-auto md:max-w-none md:flex-1 overflow-hidden"
        >
          <Tooltip
            title={`${
              category.name.charAt(0).toUpperCase() + category.name.slice(1)
            }`}
            placement="top"
            open={isTruncated ? undefined : false} // show only if truncated
          >
            <p
              ref={titleRef}
              className="font-semibold text-gray-900 truncate cursor-default"
            >
              {index + 1}.{" "}
              {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
            </p>
          </Tooltip>
        </div>

        {/* RIGHT — Sort Filter (50%) */}
        {category?.products?.length !== 0 && (
          <div
            className="basis-[60%]
      max-w-[60%]
      md:basis-auto md:max-w-none
      flex justify-end
      shrink"
          >
            <div
              className="
      flex items-center justify-end
      bg-gray-100 rounded-full
      text-[10px] text-gray-600
      px-1 py-[2px]
      gap-[2px]
      whitespace-nowrap
        max-w-full
    "
            >
              {/* Prefix */}
              <span className="ml-1 mr-[2px] text-gray-500">Sort:</span>

              {/* Best Value */}
              <button
                onClick={() => setSortBy("bestValue")}
                className={`flex items-center gap-[2px] h-[22px] px-2 rounded-full text-[10px] leading-none transition
                ${
                  sortBy === "bestValue"
                    ? "bg-teal-400 text-black"
                    : "text-gray-400"
                }`}
              >
                <span className="whitespace-nowrap">Best value</span>

                {/* Icon slot (always present) */}

                <Tooltip
                  title="Smartly sorted for the best bang for your buck!"
                  className="flex items-center justify-center w-[14px] h-[14px]"
                >
                  <InfoCircleOutlined style={{ fontSize: "12px" }} />
                </Tooltip>
              </button>

              {/* Divider */}
              <span className="text-gray-400 text-[9px]">|</span>

              {/* Cheapest */}
              <button
                onClick={() => setSortBy("cheapest")}
                className={`px-2 py-[2px] rounded-full transition h-[22px]
        ${sortBy === "cheapest" ? "bg-teal-400 text-black" : "text-gray-400"}`}
              >
                Budget
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ position: "relative" }}>
        {showLeftButton && (
          <Button
            shape="circle"
            icon={<LeftCircleFilled style={{ fontSize: "24px" }} />}
            onClick={() => scroll("left")}
            style={{
              position: "absolute",
              left: "-12px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white",
              border: "1px solid #d9d9d9",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          />
        )}

        {showRightButton && (
          <Button
            shape="circle"
            icon={<RightCircleFilled style={{ fontSize: "24px" }} />}
            onClick={() => scroll("right")}
            style={{
              position: "absolute",
              right: "-12px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white",
              border: "1px solid #d9d9d9",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          />
        )}

        <div
          ref={scrollContainerRef}
          style={{
            display: "flex",
            overflowX: "auto",
            scrollBehavior: "smooth",
            paddingBottom: "8px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
          className="hide-scrollbar"
        >
          {category?.products?.length === 0 && "No product found"}
          {dataToShow?.map((product, index) => {
            const uniqueId = `${category.id}-${product.key || product.id}`;
            const isSelected = selectedProducts.some(
              (p) => p.uniqueId === uniqueId
            );
            const isDisabled = maxSelectionReached && !isSelected;

            return (
              <ProductItemV2
                key={uniqueId}
                product={product}
                isHighlighted={index === 0}
                sortBy={sortBy}
                addToCard={(data) => {
                  // Pass additional product info to parent
                  addToCard(data)
                }}
              />
            );

            // return (
            //   <ProductCard
            //     key={product.key || product.id}
            //     product={product}
            //     categoryId={category.id}
            //     selected={isSelected}
            //     isDisabled={isDisabled}
            //     onToggle={onToggleProduct}
            //     index={index}
            //   />
            // );
          })}
        </div>
      </div>
    </div>
  );
};

export default function ShoppingList({
  loadingSkeleton: initialLoadingSkeleton = false,
  phone,
  productId,
  shopperooRender = false,
  onRenderComplete,
}) {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [jotURLInitialized, setJotURLInitialized] = useState(false);
  const [loadingSkeleton, setLoadingSkeleton] = useState(
    initialLoadingSkeleton
  );
  const [newProductList, setNewProductList] = useState([]);

  let { phoneNumber, id, adminView } = useParams();

  if (adminView) {
    shopperooRender = true;
  }

  const isDesktop = useMediaQuery("(min-width:768px)");
  const smallheightDevice = useMediaQuery("(max-height:690px)");
  const mediumHeightDEvice = useMediaQuery("(max-height:760px)");

  const [data, setData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [searchedStore, setSearchedStore] = useState([]);
  const [loading, setLoading] = useState(false);
  const [defaultStore, setDefaultStore] = useState("");

  const MAX_SELECTION = 30;
  const ASSOCIATE_TAG = "shopperoo0f-20";


  const [snackData, setSnackData] = useState({
    show: false,
    message: "",
    type: "error",
  });

  const hasCalledOnRenderRef = useRef(false);
  const trackingTimeoutRef = useRef({});

  useEffect(() => {
    if (
      phoneNumber &&
      id &&
      !loadingSkeleton &&
      productData &&
      productData.length > 0
    ) {
      const categoriesData = productData.map((category) => ({
        category_name: category.category_name || category.name,
        products:
          category.products?.map((p) => ({
            product_id: p.key,
            product_title: p.title,
            store: p.store,
            price: p.price,
            unit_price: p.unit_price,
          })) || [],
      }));

      if (!adminView) {
        // Track with session tracking service
        shoppingListTrackingService.trackListView(
          phoneNumber,
          id,
          categoriesData
        );

        console.log("Session ID:", shoppingListTrackingService.getSessionId());
        console.log("Visitor ID:", shoppingListTrackingService.getVisitorId());
      }
    }
  }, [phoneNumber, id, loadingSkeleton, productData]);

  useEffect(() => {
    // Only call onRenderComplete once when data is first loaded
    if (
      !loadingSkeleton &&
      onRenderComplete &&
      !hasCalledOnRenderRef.current &&
      productData
    ) {
      hasCalledOnRenderRef.current = true;
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        onRenderComplete();
      }, 100);
    }
  }, [loadingSkeleton, productData]);

  useEffect(() => {
    // Only call onRenderComplete once when data is first loaded
    if (loadingSkeleton) {
      hasCalledOnRenderRef.current = true;
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        onRenderComplete();
      }, 100);
    }
  }, [loadingSkeleton]);

  useEffect(() => {
    setLoadingSkeleton(initialLoadingSkeleton);
  }, [initialLoadingSkeleton]);

  if (!phoneNumber && !id && !loadingSkeleton) {
    phoneNumber = phone;
    id = productId;
  }

  useEffect(() => {
    if (!phoneNumber && !id) return;
    setLoading(true);
    UIRecommendationAPIService.shoppingList(phoneNumber, id)
      .then((response) => {
        setData(response || {});
        setProductData(response?.searched_products || []);
        setSearchedStore(response?.store_included);
        // Hide skeleton when data is loaded
        setLoadingSkeleton(false);
      })
      .catch((err) => {
        console.error("Error fetching product data:", err);
        setLoadingSkeleton(false);
      })
      .finally(() => setLoading(false));
  }, [phoneNumber, id]);

  const previousQuantitiesRef = useRef({});

  // Track quantity selection
  const handleQuantityTracking = (
    productKey,
    quantity,
    price,
    categoryData,
    upc
  ) => {
    if (!phoneNumber || !id || !categoryData || adminView) return;

    const previousQuantity = previousQuantitiesRef.current[productKey] || 0;
    if (previousQuantity === quantity) {
      return;
    }

    previousQuantitiesRef.current[productKey] = quantity;

    if (trackingTimeoutRef.current[productKey]) {
      clearTimeout(trackingTimeoutRef.current[productKey]);
    }

    trackingTimeoutRef.current[productKey] = setTimeout(() => {
      shoppingListTrackingService.trackQuantitySelection(phoneNumber, id, {
        category: categoryData.category_name,
        product_id: productKey,
        product_title: categoryData.product_title,
        quantity: quantity,
        store: categoryData.store,
        price: price,
        unit_price: categoryData.unit_price,
        upc: upc
      });
    }, 500);
  };
  // ADDED: Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts
      Object.values(trackingTimeoutRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      // Clear previous quantities
      previousQuantitiesRef.current = {};
    };
  }, []);

  useEffect(() => {
    try {
      initializeJotURL(
        "murtaza.salumber@meetpoppins.com",
        "57620ea2ea42a1a206857e062a786bfb",
        "cd09f1441b45e288ed91d84c5f6f6196"
      );
      setJotURLInitialized(true);
      console.log("✅ JotURL service initialized");
    } catch (error) {
      console.error("❌ Failed to initialize JotURL:", error);
    }
  }, []);

  const toggleProduct = (uniqueId, product) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.uniqueId === uniqueId);

      if (exists) {
        return prev.filter((p) => p.uniqueId !== uniqueId);
      } else {
        if (prev.length >= MAX_SELECTION) {
          message.warning(`You can only select up to ${MAX_SELECTION} items`);
          return prev;
        }
        return [
          ...prev,
          {
            uniqueId,
            product_key: product.key || product.id,
            ...product,
          },
        ];
      }
    });
  };

  const createAmazonCartLink = (products) => {
    if (!products || products.length === 0) {
      return null;
    }

    if (phoneNumber && id && productData) {
      const selectedProductsForTracking = [];
      const categoriesIncluded = [];

      products.forEach((productInCart) => {
        productData.forEach((category) => {
          const foundProduct = category.products?.find(
            (p) => p.key === productInCart.productKey
          );
          if (foundProduct) {
            selectedProductsForTracking.push({
              product_id: foundProduct.key,
              product_title: foundProduct.title,
              quantity: productInCart.quantity,
              price: productInCart.price,
            });

            const categoryName = category.category_name || category.name;
            if (!categoriesIncluded.includes(categoryName)) {
              categoriesIncluded.push(categoryName);
            }
          }
        });
      });

      const totalPrice = selectedProductsForTracking.reduce(
        (sum, p) => sum + p.price * p.quantity,
        0
      );

      // Use session tracking service instead
      if (!adminView) {
        shoppingListTrackingService.trackAmazonButtonClick(phoneNumber, id, {
          total_items: selectedProductsForTracking.length,
          total_price: totalPrice,
          categories_included: categoriesIncluded,
          products: selectedProductsForTracking,
        });
      }
    }

    const baseUrl = "https://www.amazon.com/gp/aws/cart/add.html";
    const params = new URLSearchParams();
    params.append("AssociateTag", ASSOCIATE_TAG);

    products.forEach((product, index) => {
      const asinNumber = index + 1;
      params.append(`ASIN.${asinNumber}`, product?.productKey);
      params.append(`Quantity.${asinNumber}`, product?.quantity || 1);
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const openKrogerAuthWindow = ({payload}) => {
    const krogerUrl =
    `${process.env.REACT_APP_KROGER_AUTH_API}&client_id=${process.env.REACT_APP_KROGER_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_KROGER_CALLBACK_URL}?products=${JSON.stringify(payload)}`;
    window.open(
      krogerUrl,
      "kroger-auth",
    );
  };

  const handleLinkOpen = (deepLink) => {
    const win = window.open(deepLink, "_blank", "noopener,noreferrer");

    if (!win) {
      // Safari popup blocked → fallback
      window.location.href = deepLink;
    }
    setLoading(false);
    setIsProcessing(false);
  };

  const handleAddToCart = async () => {
    console.log("newProductList", newProductList.length, defaultStore);


    if (newProductList?.length === 0) {
      message.warning("Please select at least one item");
      setSnackData({
        show: true,
        message: "Please select at least 1 item to add to your Amazon cart",
        type: "error",
      });
      return;
    }

    if(defaultStore.toLocaleLowerCase() === "kroger") {
      const krogerCart = newProductList.map(({ upc, quantity }) => ({ upc, quantity }));
      const payload = {
        items: krogerCart
      }
      openKrogerAuthWindow({payload})

    } else {
      setLoading(true);
      setIsProcessing(true);

      try {
        const amazonCartUrl = createAmazonCartLink(newProductList);
        console.log("Amazon Cart URL:", amazonCartUrl);

        const jotURLService = getJotURLInstance();
        const deepLink = await jotURLService.createShortLink(amazonCartUrl);

        console.log("Deep Link:", deepLink);

        if (deepLink) {
          message.success("Redirecting to Amazon cart...");
          // console.log("deepLink", deepLink);
          handleLinkOpen(deepLink);
          // window.location.href = deepLink;
          // if (!shopperooRender) {
          //   window.location.href = deepLink;
          // } else {
          //   window.open(deepLink, "_blank", "noopener,noreferrer");
          // }
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        message.error("Failed to add items to cart. Please try again.");
        setIsProcessing(false);
      }
    }

  };

  useEffect(() => {
    if (productData && productData.length > 0) {
      const defaultSelections = productData
        .filter((category) => category.products && category.products.length > 0)
        .map((category) => {
          const firstProduct = category.products[0];
          const uniqueId = `${category.id}-${
            firstProduct.key || firstProduct.id
          }`;

          return {
            uniqueId,
            product_key: firstProduct.key || firstProduct.id,
            ...firstProduct,
          };
        });

      if (selectedProducts.length === 0 && defaultSelections.length > 0) {
        setSelectedProducts(defaultSelections);
      }
    }
  }, [productData]);

  useEffect(() => {
    // console.log("Selected Products:", selectedProducts);
    // console.log(
    //   "Selected Product Keys:",
    //   selectedProducts.map((p) => p.product_key)
    // );
    // console.log("Total Selected:", selectedProducts.length);
  }, [selectedProducts]);

  const handleBackToMessages = () => {
    phoneNumber = process.env.REACT_APP_ROO_PHONE;
    if (phoneNumber) {
      window.location.href = `sms:${phoneNumber}`;
    } else {
      window.location.href = "sms:";
    }
  };

  // Show loading screen only when fetching initial data (not skeleton)
  if (loading && !loadingSkeleton) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 2,
        }}
      >
        <Box
          sx={{
            fontSize: 60,
            color: "#ff007f",
            animation: "bounce 1s infinite",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-10px)" },
            },
          }}
        >
          <img
            src={logoIcon}
            style={{ width: "60px", height: "60px" }}
            alt="Loading"
          />
        </Box>

        <div
          style={{
            fontSize: "16px",
            fontWeight: "400",
            color: "#333",
            textAlign: "center",
            maxWidth: "400px",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          }}
        >
          {isProcessing
            ? "Building your cart now…"
            : "Displaying your results…"}
        </div>
      </Box>
    );
  }

  const selectedCount = selectedProducts.length;
  const maxSelectionReached = selectedCount >= MAX_SELECTION;

  const getUniqueProducts = (categories) => {
    const seenKeys = new Set();

    return categories.map((category) => {
      const uniqueProducts =
        category.products?.filter((product) => {
          const key = product.key;

          if (seenKeys.has(key)) {
            return false;
          }

          seenKeys.add(key);
          return true;
        }) || [];

      return {
        ...category,
        products: uniqueProducts,
      };
    });
  };

  if (loadingSkeleton) {
    return <CategorySectionSkeleton />;
  }

  return (
    <div
      className="shopping_list_render"
      style={{
        // maxHeight: isDesktop ? "85vh" : smallheightDevice? "70vh" : mediumHeightDEvice ? "73vh" :"75vh",
        backgroundColor: "white",
        padding: "16px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <>
        {maxSelectionReached && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <Text style={{ fontSize: "14px", color: "#d46b08" }}>
              ⚠️ Maximum {MAX_SELECTION} items selected. Deselect an item to
              choose a different one.
            </Text>
          </div>
        )}
      </>

      {searchedStore.length > 0 && !defaultStore ? (
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-sm font-semibold text-gray-900">
            Found multiple stores for the same product
          </p>
          <p className="text-sm text-gray-700">Tell me your favourite store</p>

          <div className="flex gap-2 flex-wrap">
            {searchedStore.map((item) => {
              return (
                <button
                  className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-100"
                  key={item}
                  onClick={() => {
                    setDefaultStore(item);
                  }}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {getUniqueProducts(productData || [])?.map((category, index) => {
            return (
              <div>
                {index === 0 && (
                  <>
                    {data?.amazon_savings_amount > 0 &&
                      data?.amazon_savings_percent >= 20 && (
                        <div className=" flex flex-1 items-center gap-2 mb-4 px-4 py-3 rounded-xl bg-[#4FD1C5] text-black shadow-[0_2px_8px_rgba(0,0,0,0.12)] min-w-0">
                          <span className="text-[13px] font-semibold break-words">
                            💰 ${data?.amazon_savings_amount} saved vs Amazon's
                            first picks
                          </span>
                        </div>
                      )}
                    <div className="flex flex-wrap sm:flex-nowrap gap-3 mb-4">
                      <div className="w-1/2 sm:w-auto flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-black shadow-[0_2px_8px_rgba(0,0,0,0.12)] min-w-0 text-center">
                        <div>
                          <span className="block text-sm font-semibold">
                            ▽ Sorted by price per unit
                          </span>
                          <span className="block text-xs text-gray-500">
                            No more pack size tricks!
                          </span>
                        </div>
                      </div>

                      <div
                        className="
    w-1/2 sm:w-auto flex-1
    flex items-center justify-center gap-2
    px-4 py-3 rounded-xl
    text-black
    shadow-[0_2px_8px_rgba(0,0,0,0.12)]
    min-w-0
    text-center
  "
                      >
                        <div>
                          <span className="block text-sm font-semibold">
                            🛒 One-click add to cart
                          </span>
                          <span className="block text-xs text-gray-500">
                            Pick what you want. Add all to cart in one click!
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <CategorySection
                  key={category.id}
                  index={index}
                  category={category}
                  selectedProducts={selectedProducts}
                  onToggleProduct={toggleProduct}
                  defaultStore={defaultStore}
                  maxSelectionReached={maxSelectionReached}
                  addToCard={({
                    productKey,
                    quantity,
                    price,
                    shouldClear,
                    productTitle,
                    store,
                    unitPrice,
                    upc
                  }) => {
                    setNewProductList((prev) => {
                      if (shouldClear) return [];
                      const existingIndex = prev.findIndex(
                        (item) => item.productKey === productKey
                      );

                      // Track quantity change (including removal when quantity is 0)
                      if (productKey && !shouldClear) {
                        handleQuantityTracking(productKey, quantity, price, {
                          category_name:
                            category.category_name || category.name,
                          product_title: productTitle,
                          store: store,
                          unit_price: unitPrice,
                          upc: upc
                        });
                      }

                      // Remove if quantity is 0
                      if (quantity === 0) {
                        if (existingIndex === -1) return prev;
                        return prev.filter(
                          (item) => item.productKey !== productKey
                        );
                      }

                      // Update existing
                      if (existingIndex !== -1) {
                        const updated = [...prev];
                        updated[existingIndex] = {
                          ...updated[existingIndex],
                          quantity: quantity,
                          price: price,
                          upc: upc
                        };
                        return updated;
                      }

                      return [
                        ...prev,
                        {
                          productKey: productKey,
                          quantity: quantity,
                          price: price,
                          upc:upc
                        },
                      ];
                    });
                  }}
                />
                {index === 0 ? (
                  <div
                    className="
              inline-flex items-center gap-1
              text-[10px] text-gray-700
              px-2 py-2
              rounded-[10px]
              bg-white
              shadow-[0_2px_8px_rgba(0,0,0,0.12)]
              mb-3 mt-2
              whitespace-nowrap
            "
                  >
                    <span className="font-semibold text-gray-900">
                      🏆 Best Value
                    </span>

                    <span className="text-gray-500">
                      = lowest $/unit among most relevant products
                    </span>
                  </div>
                ) : (
                  <div style={{ marginBottom: "10px" }} />
                )}
              </div>
            );
          })}
          <div className="sticky bottom-0 z-[200] bg-white">
            <div className="max-w-[1200px]px-4 py-3">
              <FooterSummary
                newProductList={newProductList}
                amazon_savings_amount={data?.amazon_savings_amount}
                handleAddToCart={handleAddToCart}
                defaultStore={defaultStore}
                handleBackToMessages={handleBackToMessages}
                shopperooRender={shopperooRender}
                amazon_savings_percent={data?.amazon_savings_percent}
              />
            </div>
          </div>

          <Snackbar
            style={{ top: "80px" }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={snackData.show}
            autoHideDuration={3000}
            onClose={() => setSnackData({ show: false })}
          >
            <Alert
              onClose={() => setSnackData({ show: false })}
              severity={snackData.type}
            >
              {snackData.message}
            </Alert>
          </Snackbar>

        </>
      )}

      {/* Bottom buttons - hide when skeleton is loading */}
      {/* {!loadingSkeleton && (
        <div style={{
          position: 'sticky',
          bottom: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginTop: '32px',
          zIndex: 111,
          width: isDesktop ? shopperooRender ? "50%" :"35%" : "100%",
          justifySelf: "center"
        }}>
          <Button
            type="primary"
            size="large"
            block
            disabled={isProcessing}
            loading={isProcessing}
            onClick={handleAddToCart}
            style={{
              backgroundColor: '#00D9D9',
              borderColor: '#00D9D9',
              height: '56px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '12px',
              color: "black",
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {isProcessing
              ? 'PROCESSING...'
              : `ADD SELECTED ITEMS TO AMAZON CART`
            }
          </Button>
          {!shopperooRender && (
            <Button
              size="large"
              block
              onClick={handleBackToMessages}
              style={{
                height: '56px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none'
              }}
            >
              ← BACK TO ROO
            </Button>
          )}

          <Snackbar
            style={{ top: "80px" }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={snackData.show}
            autoHideDuration={3000}
            onClose={() => setSnackData({ show: false })}
          >
            <Alert
              onClose={() => setSnackData({ show: false })}
              severity={snackData.type}
            >
              {snackData.message}
            </Alert>
          </Snackbar>
        </div>
      )} */}
    </div>
  );
}
